import {
    InscalarProvider as Provider,
    useInscalar,
} from '@ilg-core/inscalar-web/react'
import { useInscalarIdentity } from '@ilg-core/inscalar-web/react'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { InscalarSDK } from '@ilg-core/inscalar-web'
import type { InscalarConfig, LogAdapter } from '@ilg-core/inscalar-web'
import { captureException } from '@sentry/nextjs'
import { useRouter } from 'next/router'

import { SENTRY_TAG_INSCALAR } from '@constants/sentryTags'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogInscalarEvent from '@hooks/useLogInscalarEvent'

import { getInscalarAppEnv } from '@utils/getInscalarAppEnv'
import { getPathnameFromAsPath } from '@utils/getPathnameFromAsPath'
import {
    getCookieYesConsent,
    getHasAnalyticsConsent,
    getJourneyCategoryFromJourney,
    getJourneyFromUrl,
    getOrSetOriginalJourney,
    getStepFromUrl,
} from '@utils/inscalar'

const logAdapter: LogAdapter = {
    debug: (message: string, context) => {
        console.debug(message, context)
    },
    info: (message: string, context) => {
        console.info(message, context)
    },
    warn: (message: string, context) => {
        console.warn(message, context)
    },
    error: (message: string, context) => {
        console.error(message, context)
    },
}

const inscalarConfig: InscalarConfig = {
    projectId: process.env.NEXT_PUBLIC_INSCALAR_PROJECT_ID ?? '',
    apiKey: '_',
    environment: getInscalarAppEnv(),
    apiBaseUrl: process.env.NEXT_PUBLIC_INSCALAR_API_BASE_URL ?? '',
    logAdapter,
    onError: (error, context) => {
        captureException(error, {
            tags: { [SENTRY_TAG_INSCALAR]: true },
            extra: context,
        })
    },
    debug: process.env.NEXT_PUBLIC_APP_ENV !== 'production',
}

function InscalarConsentSync({ children }: PropsWithChildren) {
    const { isInitialized } = useInscalar()
    const [isConsentSynced, setIsConsentSynced] = useState(false)

    useEffect(() => {
        if (!isInitialized) return

        const syncConsent = async () => {
            const consent = getCookieYesConsent()

            if (!consent) {
                if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
                    await InscalarSDK.setConsent(true, '2.1')
                } else {
                    console.warn('[Inscalar no consent]')
                }

                setIsConsentSynced(true)
                return
            }

            const hasConsent = getHasAnalyticsConsent(consent)
            await InscalarSDK.setConsent(hasConsent, '2.1')

            setIsConsentSynced(true)
        }

        syncConsent()

        window.addEventListener('cookieyes_consent_update', syncConsent)

        return () => {
            window.removeEventListener('cookieyes_consent_update', syncConsent)
        }
    }, [isInitialized])

    if (!isConsentSynced) return null

    return <>{children}</>
}

export function InscalarPageviewTracker() {
    const router = useRouter()
    const { isInitialized } = useInscalar()
    const previousPathRef = useRef<string | null>(null)
    const { logPageView } = useLogInscalarEvent()
    const { appQueryState } = useAppQueryState()

    useEffect(() => {
        if (!isInitialized) return

        const trackPageview = (url: string) => {
            const currentStep = getStepFromUrl(getPathnameFromAsPath(url))
            const previousStep = previousPathRef.current ?? undefined

            const currentJourney = getJourneyFromUrl(url)
            const currentJourneyCategory =
                getJourneyCategoryFromJourney(currentJourney)

            const originalJourney = getOrSetOriginalJourney(
                currentJourney,
                currentJourneyCategory
            )

            logPageView({
                journey: currentJourney,
                journeyCategory: currentJourneyCategory,
                originalJourney: originalJourney.journey,
                originalJourneyCategory: originalJourney.journeyCategory,
                step: currentStep,
                previousStep,
            })

            previousPathRef.current = currentStep
        }

        if (previousPathRef.current === null) {
            trackPageview(router.asPath)
        }

        const handleRouteChange = (url: string) => {
            trackPageview(url)
        }

        router.events.on('routeChangeComplete', handleRouteChange)

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [
        appQueryState.categoryId,
        isInitialized,
        logPageView,
        router.asPath,
        router.events,
    ])

    return null
}

function InscalarUtm() {
    const { appQueryState } = useAppQueryState()
    const { isInitialized } = useInscalar()
    const { identify } = useInscalarIdentity()

    useEffect(() => {
        if (!isInitialized) return

        if (appQueryState.customerId) {
            identify(appQueryState.customerId)
        }

        // TODO: Will do this in a new PR
        // InscalarSDK.setUtmParameters()
    }, [isInitialized])

    return null
}

export const InscalarProvider = ({ children }: PropsWithChildren) => {
    return (
        <Provider config={inscalarConfig}>
            <InscalarConsentSync>
                <InscalarUtm />
                <InscalarPageviewTracker />
            </InscalarConsentSync>
            {children}
        </Provider>
    )
}
